import { ExternalLink, Calendar, Tv, Trophy, Timer } from "lucide-react";

// Racing Sponsors & Schedules Panel - For Auto Racing & Sprint Car Racing Rooms
const RacingSponsorsPanel = ({ roomId }) => {
  // Only show in racing rooms
  if (roomId !== "auto-racing" && roomId !== "sprint-car-racing") return null;

  // Ways to Watch - Streaming Partners
  const streamingPartners = [
    {
      id: "floracing",
      name: "Flo Racing",
      logo: "https://customer-assets.emergentagent.com/job_yafukkinhoo-chat/artifacts/kx7dt6ex_FLO%20RACING.png",
      website: "https://www.floracing.com"
    },
    {
      id: "calidirt",
      name: "CaliDirt TV",
      logo: "https://customer-assets.emergentagent.com/job_yafukkinhoo-chat/artifacts/lyoisqva_CaliDirtTv.png",
      website: "https://calidirt.tv/"
    }
  ];

  // Racing Series We Race
  const racingSeries = [
    {
      id: "highlimit",
      name: "High Limit Racing",
      logo: "https://customer-assets.emergentagent.com/job_yafukkinhoo-chat/artifacts/d5eir0m3_HighLimit.png",
      website: "https://www.highlimitracing.com/",
      schedule: "https://customer-assets.emergentagent.com/job_yafukkinhoo-chat/artifacts/89d0wrw8_2026%20High%20Limit%20Racing%20SCHEDULE.pdf"
    },
    {
      id: "narc",
      name: "NARC 410",
      logo: "https://customer-assets.emergentagent.com/job_yafukkinhoo-chat/artifacts/qluyxxqj_NARC.png",
      website: "https://www.narc410.com",
      schedule: "https://customer-assets.emergentagent.com/job_yafukkinhoo-chat/artifacts/gfl6p2lv_NARC%202026%20schedule.pdf"
    },
    {
      id: "scct",
      name: "Sprint Car Challenge Tour",
      logo: "https://customer-assets.emergentagent.com/job_yafukkinhoo-chat/artifacts/2bcb35yk_SCCT.png",
      website: "https://www.sprintcarchallengetour.com/",
      schedule: "https://customer-assets.emergentagent.com/job_yafukkinhoo-chat/artifacts/cu3l7vww_SCCT%202026%20SCHEDULE.pdf"
    }
  ];

  return (
    <div className="retro-window mb-2" data-testid="racing-sponsors-panel">
      <div className="retro-title-bar px-2 py-1">
        <span>🏎️ RACING INFO</span>
      </div>
      
      {/* Scrollable Content Area */}
      <div className="p-3 bg-gradient-to-b from-gray-100 to-gray-200 max-h-[400px] overflow-y-auto retro-scrollbar">
        
        {/* RACE MONITOR - Live Timing with Iframe */}
        <div className="mb-4">
          <a 
            href="https://race-monitor.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block w-full p-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 rounded-lg text-white text-center transition-all hover:scale-[1.02] shadow-lg"
          >
            <div className="flex items-center justify-center gap-2">
              <Timer className="w-5 h-5" />
              <span className="font-bold text-lg">RACE MONITOR</span>
            </div>
            <p className="text-green-100 text-xs mt-1">Live Race Timing & Results - Click to Open Full Site</p>
          </a>
          
          {/* Embedded Race Monitor Iframe */}
          <div className="mt-2 bg-white rounded border border-gray-300 overflow-hidden">
            <iframe
              src="https://race-monitor.com"
              title="Race Monitor - Live Timing"
              className="w-full h-64"
              style={{ border: 'none' }}
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-popups"
            />
            <p className="text-[10px] text-center text-gray-500 py-1 bg-gray-100">
              Live race timing powered by race-monitor.com
            </p>
          </div>
        </div>
        
        {/* WAYS TO WATCH SECTION */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2 pb-1 border-b-2 border-blue-500">
            <Tv className="w-4 h-4 text-blue-600" />
            <h3 className="font-bold text-sm text-blue-800">WAYS TO WATCH OUR RACES LIVE</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {streamingPartners.map(partner => (
              <a 
                key={partner.id}
                href={partner.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block p-2 bg-black rounded-lg hover:opacity-90 transition-all hover:scale-105 cursor-pointer"
                title={`Watch on ${partner.name}`}
              >
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className="h-10 mx-auto object-contain"
                />
              </a>
            ))}
          </div>
        </div>

        {/* RACING SERIES SECTION */}
        <div>
          <div className="flex items-center gap-2 mb-2 pb-1 border-b-2 border-red-500">
            <Trophy className="w-4 h-4 text-red-600" />
            <h3 className="font-bold text-sm text-red-800">RACING SERIES WE RACE</h3>
          </div>
          
          <div className="space-y-3">
            {racingSeries.map(series => (
              <div key={series.id} className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
                <div className="flex items-center gap-3">
                  {/* Logo - Clickable to Website */}
                  <a 
                    href={series.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
                    title={`Visit ${series.name} Website`}
                  >
                    <img 
                      src={series.logo} 
                      alt={series.name}
                      className="h-12 w-20 object-contain hover:scale-105 transition-transform"
                    />
                  </a>
                  
                  <div className="flex-1 min-w-0">
                    {/* Website Link */}
                    <a 
                      href={series.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1 mb-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Official Site
                    </a>
                    
                    {/* 2026 Schedule Link */}
                    <a 
                      href={series.schedule}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white text-[10px] font-bold rounded shadow transition-all"
                    >
                      <Calendar className="w-3 h-3" />
                      2026 SCHEDULE
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RacingSponsorsPanel;
